const BULLET_RE = /^[•\-–*]\s/

function formatAssistantBlocks(content) {
  if (!content || typeof content !== 'string') return []
  return content.split(/\n\n+/).map((b) => b.trim()).filter(Boolean)
}

/** Render **bold** and *italic* inline formatting */
function renderInline(text) {
  const parts = []
  let remaining = text
  let keyIdx = 0

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/)

    const boldIdx = boldMatch ? remaining.indexOf(boldMatch[0]) : Infinity
    const italicIdx = italicMatch ? remaining.indexOf(italicMatch[0]) : Infinity

    if (boldIdx === Infinity && italicIdx === Infinity) {
      parts.push(remaining)
      break
    }

    if (boldIdx <= italicIdx && boldMatch) {
      if (boldIdx > 0) parts.push(remaining.slice(0, boldIdx))
      parts.push(<strong key={keyIdx++} className="font-semibold text-gray-900">{boldMatch[1]}</strong>)
      remaining = remaining.slice(boldIdx + boldMatch[0].length)
    } else if (italicMatch) {
      if (italicIdx > 0) parts.push(remaining.slice(0, italicIdx))
      parts.push(<em key={keyIdx++} className="text-gray-600">{italicMatch[1]}</em>)
      remaining = remaining.slice(italicIdx + italicMatch[0].length)
    }
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts
}

function stripBulletPrefix(line) {
  return line.replace(/^[•\-–*]\s+/, '')
}

/**
 * Clean, readable assistant message rendering.
 * Minimal chrome — the text should do the talking.
 */
export function SetupAssistantMessage({ content }) {
  const blocks = formatAssistantBlocks(content)

  return (
    <div className="space-y-3 text-[15px] leading-[1.65] text-gray-700">
      {blocks.map((block, i) => {
        const lines = block.split('\n').map((l) => l.trimEnd())
        const nonEmpty = lines.filter((l) => l.length > 0)

        // Single line ending with colon → subtle section header
        if (nonEmpty.length === 1 && nonEmpty[0].trim().endsWith(':')) {
          return (
            <p key={i} className="text-[13px] font-medium text-gray-500 mt-1">
              {nonEmpty[0].trim()}
            </p>
          )
        }

        // Pure bullet block
        const bulletLines = nonEmpty.filter((l) => BULLET_RE.test(l))
        if (bulletLines.length > 0 && bulletLines.length === nonEmpty.length) {
          return (
            <ul key={i} className="space-y-1.5 pl-0">
              {bulletLines.map((line, j) => (
                <li key={j} className="flex gap-2.5 text-[14px]">
                  <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-gray-400" aria-hidden />
                  <span className="text-gray-700">{renderInline(stripBulletPrefix(line))}</span>
                </li>
              ))}
            </ul>
          )
        }

        // Mixed block
        if (bulletLines.length > 0 && bulletLines.length < nonEmpty.length) {
          const elements = []
          let bulletGroup = []

          const flushBullets = () => {
            if (bulletGroup.length === 0) return
            elements.push(
              <ul key={`${i}-ul-${elements.length}`} className="space-y-1.5 pl-0">
                {bulletGroup.map((line, j) => (
                  <li key={j} className="flex gap-2.5 text-[14px]">
                    <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-gray-400" aria-hidden />
                    <span className="text-gray-700">{renderInline(stripBulletPrefix(line))}</span>
                  </li>
                ))}
              </ul>
            )
            bulletGroup = []
          }

          for (const line of nonEmpty) {
            if (BULLET_RE.test(line)) {
              bulletGroup.push(line)
            } else {
              flushBullets()
              if (line.trim().endsWith(':')) {
                elements.push(
                  <p key={`${i}-h-${elements.length}`} className="text-[13px] font-medium text-gray-500 mt-1">
                    {line.trim()}
                  </p>
                )
              } else {
                elements.push(
                  <p key={`${i}-p-${elements.length}`}>{renderInline(line)}</p>
                )
              }
            }
          }
          flushBullets()

          return <div key={i} className="space-y-1.5">{elements}</div>
        }

        // Plain text
        return (
          <p key={i} className="whitespace-pre-wrap">
            {renderInline(block)}
          </p>
        )
      })}
    </div>
  )
}

export default SetupAssistantMessage
